// ============================================================
// SWISS N SCOTT — Global Design Variables
// All colors, fonts, spacing, and breakpoints in one place
// ============================================================

export const colors = {
  // Primary Palette
  cream:        "#FAF8F5",
  warmWhite:    "#FFFFFF",
  beige:        "#F0EBE3",
  beigeLight:   "#F7F4F0",
  sand:         "#E8DFD0",
  sandDark:     "#D4C4AD",

  // Accent
  gold:         "#B8965A",
  goldLight:    "#D4AF7A",
  goldDark:     "#8B6E3A",
  goldGlow:     "rgba(184,150,90,0.15)",

  // Neutrals
  charcoal:     "#2C2C2C",
  darkGrey:     "#4A4A4A",
  midGrey:      "#6B6B6B",
  lightGrey:    "#9B9B9B",
  borderGrey:   "#E0D8CC",
  bgGrey:       "#F4F1ED",

  // Semantic
  white:        "#FFFFFF",
  black:        "#1A1A1A",
  overlay:      "rgba(28,24,18,0.52)",
  overlayLight: "rgba(28,24,18,0.30)",
};

export const fonts = {
  heading:  "'Poppins', 'Helvetica Neue', Arial, sans-serif",
  subhead:  "'Poppins', 'Helvetica Neue', Arial, sans-serif",
  body:     "'Jost', 'Helvetica Neue', Arial, sans-serif",
  accent:   "'Poppins', Arial, sans-serif",
  mono:     "'Courier New', monospace",
};

export const fontWeights = {
  light:      300,
  regular:    400,
  medium:     500,
  semibold:   600,
  bold:       700,
};

export const fontSizes = {
  xs:   "0.75rem",   // 12px
  sm:   "0.875rem",  // 14px
  base: "1rem",      // 16px
  md:   "1.125rem",  // 18px
  lg:   "1.25rem",   // 20px
  xl:   "1.5rem",    // 24px
  "2xl":"1.875rem",  // 30px
  "3xl":"2.25rem",   // 36px
  "4xl":"3rem",      // 48px
  "5xl":"3.75rem",   // 60px
  "6xl":"4.5rem",    // 72px
};

export const spacing = {
  xs:   "0.25rem",   // 4px
  sm:   "0.5rem",    // 8px
  md:   "1rem",      // 16px
  lg:   "1.5rem",    // 24px
  xl:   "2rem",      // 32px
  "2xl":"3rem",      // 48px
  "3xl":"4rem",      // 64px
  "4xl":"6rem",      // 96px
  "5xl":"8rem",      // 128px
};

export const breakpoints = {
  xs:  "480px",
  sm:  "640px",
  md:  "768px",
  lg:  "1024px",
  xl:  "1280px",
  "2xl":"1536px",
};

export const borderRadius = {
  sm:   "6px",
  md:   "12px",
  lg:   "20px",
  xl:   "32px",
  full: "9999px",
  circle: "50%",
};

export const shadows = {
  sm:   "0 2px 8px rgba(44,36,20,0.08)",
  md:   "0 4px 20px rgba(44,36,20,0.12)",
  lg:   "0 8px 40px rgba(44,36,20,0.16)",
  xl:   "0 16px 60px rgba(44,36,20,0.20)",
  gold: "0 4px 24px rgba(184,150,90,0.25)",
  inset:"inset 0 2px 8px rgba(44,36,20,0.08)",
};

export const transitions = {
  fast:   "all 0.15s ease",
  base:   "all 0.25s ease",
  slow:   "all 0.4s ease",
  spring: "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
};

export const zIndex = {
  base:    1,
  above:   10,
  nav:     100,
  modal:   1000,
  toast:   9999,
};
// Category data used across the app
export const categories = [
  {
    id: "jacquard-weave",
    name: "Jacquard Weave\nKitchen Towels",
    shortName: "Jacquard Weave",
    color: "#8B6E3A",
    bgColor: "#F5EFE6",
    description: "Intricate woven patterns crafted with precision Jacquard looms. Our jacquard kitchen towels combine artistic design with superior absorbency.",
    specs: { material: "100% Cotton", gsm: "180–220 GSM / Custom", size: "50×70 cm / Custom" },
    products: [
      { name: "Classic Floral Jacquard",    desc: "Elegant floral pattern, soft and absorbent.",                              material: "100% Cotton", gsm: "200 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Geometric Diamond Jacquard", desc: "Modern diamond lattice design for contemporary kitchens.",                 material: "100% Cotton", gsm: "210 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Heritage Stripe Jacquard",   desc: "Traditional striped pattern with raised weave texture.",                   material: "100% Cotton", gsm: "190 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Botanical Leaf Jacquard",    desc: "Nature-inspired leaf motifs woven in soft earth tones.",                   material: "100% Cotton", gsm: "205 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Royal Border Jacquard",      desc: "Ornate border design with premium sheen finish.",                          material: "100% Cotton", gsm: "220 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Wave Pattern Jacquard",      desc: "Fluid wave design offering a modern aesthetic.",                           material: "100% Cotton", gsm: "195 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Garden Vegetable Jacquard",  desc: "Colorful vegetable and garden motifs woven in vibrant jacquard.",          material: "100% Cotton", gsm: "200 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Jacquard Mixed Kitchen Set", desc: "Coordinated jacquard kitchen set with mixed patterns and weaves.",         material: "100% Cotton", gsm: "190–220 GSM / Custom", size: "50×70 cm / Custom" },
    ]
  },
  {
    id: "dobby-weave",
    name: "Dobby Weave \nKitchen Towels",
    shortName: "Dobby Weave",
    color: "#5A7A6E",
    bgColor: "#EAF2EF",
    description: "Small geometric patterns created with dobby attachments. Our dobby weave towels offer texture, durability, and elegant simplicity.",
    specs: { material: "100% Cotton", gsm: "160–200 GSM / Custom", size: "50×70 cm / Custom" },
    products: [
      { name: "Classic Dobby Check",           desc: "Crisp checkered dobby weave, timeless kitchen staple.",                   material: "100% Cotton",        gsm: "180 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Herringbone Dobby",             desc: "Classic herringbone pattern with excellent absorbency.",                   material: "100% Cotton / Custom", gsm: "190 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Waffle Dobby",                  desc: "Waffle texture maximizes surface area for quick drying.",                  material: "100% Cotton",        gsm: "195 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Border Stripe Dobby",           desc: "Bold border stripes with a premium herringbone body.",                    material: "100% Cotton",        gsm: "185 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Yarn Dyed Waffle Dobby",        desc: "Vibrant multicolor stripes on crisp white dobby base.",                  material: "100% Cotton",        gsm: "190 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Fine Stripe Dobby",             desc: "Delicate fine stripes for a subtle, elegant kitchen look.",               material: "100% Cotton",        gsm: "170 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Mini Chess Dobby Kitchen Towel",desc: "Classic French-style blue and white mini chess pattern.",                 material: "100% Cotton",        gsm: "175 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Chess Board Dobby",             desc: "Colorful checkered dobby towels, perfect as a gift set.",                 material: "100% Cotton",        gsm: "180 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Mono Check Dobby",              desc: "Traditional mono check in a range of fresh colors.",                      material: "100% Cotton",        gsm: "178 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Waffle Stripe Dobby",           desc: "Waffle weave with refined dobby stripe detailing.",                       material: "100% Cotton",        gsm: "192 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Dobby Kitchen Set",             desc: "Coordinated dobby kitchen set with mixed patterns.",                      material: "100% Cotton",        gsm: "180–195 GSM / Custom", size: "50×70 cm / Custom" },
    ]
  },
  {
    id: "plain-weave",
    name: "Flat Weave\nKitchen Towels",
    shortName: "Flat Weave",
    emoji: "◻",
    color: "#6B7B8D",
    bgColor: "#EDF1F5",
    description: "Clean, flat-woven towels that are lightweight, fast-drying, and perfect for everyday kitchen use. Simple perfection.",
    specs: { material: "100% Cotton / Cotton-Linen Blend", gsm: "140–180 GSM / Custom", size: "50×70 cm / Custom" },
    products: [
      { name: "Natural Linen Blend",      desc: "Cotton-linen blend for rustic, natural appeal.",                              material: "55% Cotton 45% Linen", gsm: "155 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Coloured Stripe",     desc: "Bright yarn-dyed stripes on plain base weave.",                               material: "100% Cotton",          gsm: "170 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Stone Washed",        desc: "Pre-washed for instant softness and casual appeal.",                          material: "100% Cotton",          gsm: "165 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Colorful Stripe",     desc: "Vibrant hanging stripes in orange and green on flat weave.",                  material: "100% Cotton",          gsm: "160 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Yellow Plaid",        desc: "Cheerful mustard yellow plaid, ideal for gifting.",                           material: "100% Cotton",          gsm: "168 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Blue Gingham",        desc: "Classic royal blue gingham on smooth flat weave cotton.",                     material: "100% Cotton",          gsm: "162 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Multi Stripe",        desc: "White flat weave with multicolor centre stripe detailing.",                   material: "100% Cotton",          gsm: "158 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Rainbow Check",       desc: "Bold multicolor check in a vibrant rainbow palette.",                         material: "100% Cotton",          gsm: "165 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Classic Check",            desc: "Elegant thin-line windowpane check in blue, yellow and red.",                 material: "100% Cotton",          gsm: "155 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Multi Check",         desc: "Rolled and folded flat weave kitchen set, export-ready.",                     material: "100% Cotton",          gsm: "160 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Navy Mixed Set",      desc: "Five-piece navy mixed flat weave set with check, gingham & solid.",           material: "100% Cotton",          gsm: "163 GSM / Custom", size: "50×70 cm / Custom" },
    ]
  },
  {
    id: "terry-kitchen",
    name: "Terry Kitchen\nTowels",
    shortName: "Terry Towels",
    color: "#7A6B8B",
    bgColor: "#F0EDF5",
    description: "Highly absorbent terry loop construction for maximum moisture retention. Ideal for heavy-duty kitchen use and drying.",
    specs: { material: "100% Cotton", gsm: "300–450 GSM / Custom", size: "40×60 cm / Custom" },
    products: [
      { name: "Jacquard Terry Towel",             desc: "Classic black & white buffalo check terry towels stacked in a set.",       material: "100% Cotton", gsm: "380 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Multi Colour Check Terry Set",     desc: "Coordinated check terry set in brown, red and blue colourways.",           material: "100% Cotton", gsm: "360 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Classic Check Terry",              desc: "Traditional red, green and blue check terry kitchen towels.",               material: "100% Cotton", gsm: "340 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Neutral Stripe Terry Set",         desc: "Mixed neutral stripe and waffle terry set in beige and brown tones.",      material: "100% Cotton", gsm: "370 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Multi Stripe Terry",               desc: "Vivid multicolour stripes on mustard, teal, orange and black terry base.", material: "100% Cotton", gsm: "350 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Grey Stripe Terry Set",            desc: "Crisp 4-piece grey and white stripe terry kitchen towel set.",             material: "100% Cotton", gsm: "360 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Rainbow Check Terry",              desc: "Bold rainbow-coloured check pattern on a deep charcoal terry base.",       material: "100% Cotton", gsm: "345 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "White Blue Stripe Terry",          desc: "Classic white terry with clean navy blue centre stripes.",                 material: "100% Cotton", gsm: "330 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Grey Woven Stripe Reversed Terry", desc: "Premium grey woven stripe terry with plush loop reverse.",                 material: "100% Cotton", gsm: "390 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Waffle Check Terry Set",           desc: "White waffle-weave terry set with teal, red and black check options.",     material: "100% Cotton", gsm: "400 GSM / Custom", size: "40×60 cm / Custom" },
    ]
  },
  {
    id: "dish-cloth",
    name: "Dish Cloth",
    shortName: "Dish Cloth",
    color: "#8B7A5A",
    bgColor: "#F5F0E8",
    description: "Versatile dish cloths in various weaves and sizes for washing, wiping, and polishing. Durable enough for daily professional use.",
    specs: { material: "100% Cotton", gsm: "220–260 GSM / Custom", size: "30×30 cm / 35×35 cm / Custom" },
    products: [
      { name: "Check Dish Cloth",            desc: "Classic check pattern, soft and long-lasting.",                               material: "100% Cotton", gsm: "130 GSM / Custom", size: "30×30 cm / Custom" },
      { name: "Beige Grid Dish Cloth",       desc: "Elegant beige and gold windowpane grid dish cloth, soft and absorbent.",      material: "100% Cotton", gsm: "135 GSM / Custom", size: "35×35 cm / Custom" },
      { name: "Multicolor Check Dish Cloth", desc: "Bold red, blue and orange check dish cloths stacked in assorted colours.",    material: "100% Cotton", gsm: "130 GSM / Custom", size: "30×30 cm / Custom" },
      { name: "Mixed Dish Cloth Bulk Set",   desc: "Large bulk set of mixed check and stripe dish cloths in vibrant colours.",    material: "100% Cotton", gsm: "125 GSM / Custom", size: "70×70 cm / Custom" },
      { name: "Solid Dish Cloth Gift Set",   desc: "Premium navy, green and grey solid dish cloths presented as a gift set.",     material: "100% Cotton", gsm: "140 GSM / Custom", size: "35×35 cm / Custom" },
    ]
  },
  {
    id: "printed-kitchen",
    name: "Printed Kitchen Towels",
    shortName: "Printed Kitchen Towels",
    color: "#8B5A5A",
    bgColor: "#F5EBEB",
    description: "Vibrant reactive and pigment-printed designs on quality woven bases. From seasonal patterns to custom brand prints.",
    specs: { material: "100% Cotton", gsm: "160–200 GSM / Custom", size: "50×70 cm / Custom" },
    products: [
      { name: "Apple Print Kitchen Towel",  desc: "Cheerful apple motif screen-printed on soft cotton kitchen towels.",          material: "100% Cotton", gsm: "175 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Bird Print Kitchen Towel",   desc: "Decorative bird and branch print in a crisp, nature-inspired palette.",       material: "100% Cotton", gsm: "175 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Floral Block Print",         desc: "Hand-inspired floral block print in bold, export-quality ink.",               material: "100% Cotton", gsm: "175 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Floral Embroidered Print",   desc: "Delicate floral embroidery detailing on a fine woven cotton base.",           material: "100% Cotton", gsm: "185 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Fruit Appliqué Printed",     desc: "Vibrant fruit appliqué and print combination for a lively kitchen accent.",   material: "100% Cotton", gsm: "180 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Glass Cloth Printed",        desc: "Traditional glass cloth weave with a bold printed stripe or motif.",          material: "100% Cotton", gsm: "170 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Kitchen Novelty Printed",    desc: "Fun novelty kitchen-themed prints, popular for gifting and retail display.",  material: "100% Cotton", gsm: "180 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Custom Logo Printed",        desc: "Premium blank canvas for custom brand logo printing — MOQ available.",        material: "100% Cotton", gsm: "170 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Custom Text Printed",        desc: "Personalised text printing on quality kitchen towels for branded orders.",    material: "100% Cotton", gsm: "170 GSM / Custom", size: "50×70 cm / Custom" },
      { name: "Printed Kitchen Set",        desc: "Coordinated printed kitchen towel set, styled and export-ready.",             material: "100% Cotton", gsm: "175–185 GSM / Custom", size: "50×70 cm / Custom" },
    ]
  },
  {
    id: "kitchen-sets",
    name: "Kitchen Sets",
    shortName: "Kitchen Sets",
    color: "#5A7A5A",
    bgColor: "#EAF2EA",
    description: "Curated kitchen textile sets combining towels, oven mitts, and aprons in matching designs. Perfect for retail and gifting.",
    specs: { material: "100% Cotton", gsm: "Varies / Custom", size: "Multiple Sizes / Custom" },
    products: [
      { name: "Blue Check Apron Kitchen Set",      desc: "Navy blue check apron with matching oven mitts and pot holders — a complete coordinated set.",          material: "100% Cotton", gsm: "180–300 GSM / Custom", size: "Various / Custom" },
      { name: "Grey Stripe Apron Kitchen Set",     desc: "Classic grey and white ticking stripe apron, oven mitt and potholder in a neat gift set.",              material: "100% Cotton", gsm: "180–300 GSM / Custom", size: "Various / Custom" },
      { name: "Red Tartan Apron Kitchen Set",      desc: "Bold red tartan plaid apron, oven glove and pot holder — ideal for festive gifting.",                   material: "100% Cotton", gsm: "180–300 GSM / Custom", size: "Various / Custom" },
      { name: "Navy Utensil Print Kitchen Set",    desc: "Navy apron paired with utensil-motif oven mitt and potholder, styled for export retail.",               material: "100% Cotton", gsm: "180–300 GSM / Custom", size: "Various / Custom" },
      { name: "Orange Star Kitchen Set",           desc: "Vibrant orange and green star-print full kitchen set: apron, double glove, mitt, towels & potholder.",  material: "100% Cotton", gsm: "180–300 GSM / Custom", size: "Various / Custom" },
      { name: "Butterfly Print Double Oven Glove", desc: "Elegant sage green butterfly-print double oven glove with terry lining — premium export quality.",       material: "100% Cotton", gsm: "320 GSM / Custom",     size: "Standard / Custom" },
      { name: "White Half Apron",                  desc: "Clean white waist apron with a large front pocket — versatile for hospitality and catering.",            material: "100% Cotton", gsm: "200 GSM / Custom",     size: "Standard / Custom" },
      { name: "White Bib Apron",                   desc: "Classic full-length white bib apron, soft cotton, ideal for branded or plain export orders.",            material: "100% Cotton", gsm: "200 GSM / Custom",     size: "Standard / Custom" },
    ]
  },
  {
    id: "others",
    name: "Others",
    shortName: "Others",
    color: "#6B7B6B",
    bgColor: "#EFF2EF",
    description: "Oven gloves, aprons, pot holders, table runners, and more — our extended range of premium home textile products.",
    specs: { material: "100% Cotton / Blends", gsm: "Varies / Custom", size: "Standard / Custom" },
  products: [
  { 
    name: "FR HVAC Duct Wrapping Fabric", 
    desc: "Fire Retardant grey fabric used for HVAC duct insulation wrapping and industrial fire protection applications.", 
    material: "FR Cotton Blend", 
    gsm: "Varies / Custom", 
    size: "Standard / Custom" 
  },
  { name: "Baby Diaper Bleached",      desc: "Soft bleached white baby diaper fabric — absorbent and gentle for infant care.",                material: "100% Cotton", gsm: "160 GSM / Custom",     size: "Standard / Custom" },
  { name: "Baby Diaper Greige",        desc: "Natural greige baby diaper cloth — unbleached, chemical-free and export-ready.",                 material: "100% Cotton", gsm: "160 GSM / Custom",     size: "Standard / Custom" },
  { name: "Baby Diaper Printed",       desc: "Cheerful printed baby diaper fabric with playful motifs for retail packaging.",                  material: "100% Cotton", gsm: "160 GSM / Custom",     size: "Standard / Custom" },
  { name: "Bar Mop",                   desc: "Classic white bar mop towels — heavy-duty, highly absorbent and ideal for commercial use.",      material: "100% Cotton", gsm: "400 GSM / Custom",     size: "40×60 cm / Custom" },
  { name: "Bar Mop Dyed",              desc: "Colour-striped bar mop towels — dyed for easy identification in busy kitchen environments.",     material: "100% Cotton", gsm: "400 GSM / Custom",     size: "40×60 cm / Custom" },
  { name: "Bathrobe",                  desc: "Plush terry bathrobe in neutral tones — premium quality for hotel and spa export orders.",       material: "100% Cotton", gsm: "380 GSM / Custom",     size: "Standard / Custom" },
  { name: "Cheesecloth",               desc: "Fine open-weave cheesecloth — food-grade cotton for straining, wrapping and crafting.",          material: "100% Cotton", gsm: "40 GSM / Custom",      size: "Standard / Custom" },
  { name: "Futa Beach Towel",          desc: "Lightweight futa-style beach towel — quick-dry, compact and ideal for holiday retail.",          material: "100% Cotton", gsm: "200 GSM / Custom",     size: "90×180 cm / Custom" },
  { name: "Hand Towel",                desc: "Soft cotton hand towels with classic border detailing — suitable for hotel and home export.",    material: "100% Cotton", gsm: "400 GSM / Custom",     size: "50×90 cm / Custom" },
  { name: "Shop Towels",               desc: "Industrial-grade shop towels — lint-free, durable and ideal for automotive and workshop use.",   material: "100% Cotton", gsm: "130 GSM / Custom",     size: "Standard / Custom" },
  { name: "Table Cover Plain Dyed",    desc: "Smooth plain-dyed table covers in versatile colourways for hospitality and catering.",           material: "100% Cotton", gsm: "180 GSM / Custom",     size: "Standard / Custom" },
  { name: "Table Cover Printed",       desc: "Decorative printed table covers — vibrant patterns for retail, gifting and event use.",          material: "100% Cotton", gsm: "180 GSM / Custom",     size: "Standard / Custom" },
  { name: "Table Cover Yarn Dyed",     desc: "Premium yarn-dyed table covers with classic woven patterns for restaurant and hotel export.",    material: "100% Cotton", gsm: "200 GSM / Custom",     size: "Standard / Custom" },
  { name: "Yarn Dyed Wovem Garments",  desc: "Premium yarn-dyed woven garments crafted for export quality and refined home textile collections.", material: "100% Cotton", gsm: "200 GSM / Custom",     size: "Standard / Custom" },
]
  },
];
export const companyInfo = {
  name: "Swiss N Scott",
  tagline: "Premium Kitchen Towels & Home Textiles",
  subTagline: "Crafted with precision. Exported with pride.",
  established: 2004,
  certifications: ["OEKO-TEX", "Sedex", "BSCI", "ISO"],
  markets: ["European Markets", "USA Markets"],
  contact: {
    ceo: "Ghalib Qureshi",
    headOffice: "32-B Buch Executive Villas Bosan Road, Multan, Pakistan 63000",
    factory: "436/22 Export Zone, Near Ansari Chowk Shah Rukn-e-Alam, Multan, Pakistan 60600",
    emails: ["ghalib@swissnscott.pk", "info@swissnscott.pk", "qureshighalib@yahoo.com"],
    website: "www.swissnscott.pk",
    phone: "+92-61-2215490",
    mobile: "+92 300 8732191",
  }
};